/**
 * Sprint TCO-C — Candidate-facing TalentCompetency Context panel.
 *
 * Rendered ABOVE the existing assessment flow (`EndUserAssessment.jsx`) so the
 * employee understands which Competency Profile + position they are being
 * evaluated against BEFORE answering the first question. Purely additive —
 * fetches a tiny read-only context endpoint and renders nothing on 404
 * (non-TalentCompetency assignments still work unchanged).
 *
 * NO scores, NO interpretation, NO advisor comments — that surface belongs
 * to TCO-D. This panel only wraps the existing player with role context.
 */
import i18n from '@/i18n';
import { bizDate } from '@/lib/dates';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Building2, Briefcase, Target, CalendarClock, Clock, Users, ChevronDown, ChevronUp, UserCircle,
} from 'lucide-react';
import { api } from '@/lib/api';

const STATUS_TONE = {
  invited:     'bg-blue-100 text-blue-700 border-blue-200',
  in_progress: 'bg-amber-100 text-amber-700 border-amber-200',
  submitted:   'bg-green-100 text-green-700 border-green-200',
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return bizDate(iso, i18n.language); } catch { return iso; }
};

export function CompetencyContextPanel({ assignmentId }) {
  const { t } = useTranslation();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (!assignmentId) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/me/assignments/${assignmentId}/competency-context`);
        if (alive) setCtx(r.data);
      } catch (e) {
        // 404 — assignment is not a TalentCompetency engagement; render nothing.
        if (alive) setCtx(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [assignmentId]);

  if (loading || !ctx) return null;

  const status = ctx.engagement_status || 'invited';
  const tone = STATUS_TONE[status] || STATUS_TONE.invited;
  const dueLabel = fmtDate(ctx.closing_date);

  return (
    <Card className="p-5 mb-6 shadow-card border-primary/20" data-testid="tco-candidate-context-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Target className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
              {t('tco.candidate.engagementLabel', { defaultValue: 'TalentCompetency engagement' })}
            </p>
            <h2 className="font-display text-lg font-bold tracking-tight leading-tight mt-0.5" data-testid="tco-candidate-context-profile">
              {ctx.profile_name || t('tco.candidate.untitledProfile', { defaultValue: 'Competency Profile' })}
            </h2>
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
              {ctx.company_name && (
                <span className="inline-flex items-center gap-1" data-testid="tco-candidate-context-company">
                  <Building2 className="h-3.5 w-3.5" /> {ctx.company_name}
                </span>
              )}
              {ctx.position && ctx.position !== ctx.profile_name && (
                <span className="inline-flex items-center gap-1" data-testid="tco-candidate-context-position">
                  <Briefcase className="h-3.5 w-3.5" /> {ctx.position}
                </span>
              )}
              {ctx.department && (
                <span className="inline-flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" /> {ctx.department}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant="outline" className={`text-xs ${tone}`} data-testid="tco-candidate-context-status">
            {t(`tco.candidate.status.${status}`, { defaultValue: status })}
          </Badge>
          <button
            type="button"
            onClick={() => setExpanded((x) => !x)}
            className="h-8 w-8 rounded-md hover:bg-accent/40 flex items-center justify-center"
            aria-label="Toggle details"
            data-testid="tco-candidate-context-toggle"
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <>
          {ctx.profile_description && (
            <p className="text-sm text-muted-foreground mt-4 leading-relaxed" data-testid="tco-candidate-context-desc">
              {ctx.profile_description}
            </p>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <Metric
              icon={Clock}
              label={t('tco.candidate.estimatedMinutes', { defaultValue: 'Estimated time' })}
              value={ctx.estimated_minutes ? `${ctx.estimated_minutes} min` : '—'}
              testId="tco-candidate-context-duration"
            />
            <Metric
              icon={CalendarClock}
              label={t('tco.candidate.dueBy', { defaultValue: 'Due by' })}
              value={dueLabel || (t('tco.candidate.noDeadline', { defaultValue: 'No deadline' }))}
              testId="tco-candidate-context-deadline"
            />
            <Metric
              icon={UserCircle}
              label={t('tco.candidate.requestedBy', { defaultValue: 'Requested by' })}
              value={ctx.requested_by_name || '—'}
            />
            <Metric
              icon={Target}
              label={t('tco.candidate.competencies', { defaultValue: 'Competencies' })}
              value={ctx.competencies_count ?? 0}
            />
          </div>

          {ctx.assessments_total > 1 && (
            <div className="mt-5">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-muted-foreground">
                  {t('tco.candidate.progressLabel', {
                    defaultValue: '{{done}} of {{total}} assessments done',
                    done: ctx.assessments_completed || 0,
                    total: ctx.assessments_total,
                  })}
                </span>
                <span className="text-xs font-semibold tabular-nums">{ctx.average_progress || 0}%</span>
              </div>
              <Progress value={ctx.average_progress || 0} className="h-1.5" />
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function Metric({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-start gap-2" data-testid={testId}>
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="text-sm font-medium truncate">{value}</p>
      </div>
    </div>
  );
}
